import {ParameterValidator} from '@mvps-genesis/utilities';
import tippy from 'tippy.js';
import './AjaxAddToWishlist.scss';

export default class AjaxAddToWishlist {

	constructor (
		formElement,
		{
			submitButtonElements = '.js-atwl-button',
			actionInputElement = 'input[name="Action"]',
			action = 'ATWL',
			atwlQuerySelector = '#ajax_atwl_content'
		} = {},
		successCallback
	) {
		// Create validator
		this.validator = new ParameterValidator(this);

		this.formElement = this.validator.isElement('formElement', formElement);
		this.submitButtonElements = this.validator.isElementList('options.submitButtonElements', submitButtonElements, {
			container: this.formElement,
			required: false
		});

		if (!this.submitButtonElements?.length) {
			return;
		}

		this.actionInputElement = this.validator.isElement('options.actionInputElement', actionInputElement, {container: this.formElement});
		this.action = this.validator.isString('options.action', action);
		this.atwlQuerySelector = this.validator.isString('options.atwlQuerySelector', atwlQuerySelector);
		this.successCallback = this.validator.isFunction('successCallback', successCallback, {required: false});

		this.original_action = this.actionInputElement.value;

		this.currentButton = '';

		this.bindEvents();
	}

	bindEvents () {

		this.formElement.addEventListener('submit', (e) => this.formElementSubmit(e), false);

		for (let submitButton of this.submitButtonElements) {
			this.createTippy(submitButton);

			submitButton.addEventListener('click', () => this.submitButtonClicked(submitButton), false);
		}

	}

	submitButtonClicked (button) {

		if (!button._tippy) {
			return false;
		}

		this.currentButton = button;
		this.actionInputElement.value = this.action;

		return true;
	}

	async formElementSubmit (e) {

		if (this.actionInputElement.value !== this.action) {
			return;
		}

		e.preventDefault();
		e.stopImmediatePropagation();

		this.showTippy(this.loadingContent);

		const formData = new FormData(this.formElement);

		if (!formData.hasOwnProperty('WishList_ID')) {
			formData.append('WishList_ID', -1);
		}

		const response = await this.submitRequest(this.formElement.action, formData);

		this.resetAction();

		this.handleResponse(response);
	}

	async submitRequest (url, formdata) {

		const response = await fetch(url, {
			body: formdata,
			method: 'POST'
		});

		if (!response.ok) {
			throw new Error('Network response was not successful');
		}

		return response.text();
	}

	showTippy (content) {
		if (this.currentButton && this.currentButton._tippy) {

			this.currentButton._tippy.setContent(content);
			this.currentButton._tippy.show();

			return true;
		}

		for (let submitButton of this.submitButtonElements) {
			if (!this.isVisible(submitButton)) {
				continue;
			}

			if (!submitButton._tippy) {
				continue;
			}

			submitButton._tippy.setContent(content);
			submitButton._tippy.show();
		}

		return true;
	}

	hideTippy () {
		if (this.currentButton && this.currentButton._tippy) {

			this.currentButton._tippy.hide();

			return true;
		}

		for (let submitButton of this.submitButtonElements) {
			if (!this.isVisible(submitButton)) {
				continue;
			}

			if (!submitButton._tippy) {
				continue;
			}

			submitButton._tippy.hide();
		}

		return true;
	}

	loadingContent () {
		return `
		<div class="t-atwl-content">
			<div class="t-atwl-header">
				Add to a Saved List
			</div>
			<div class="t-atwl-info t-atwl-loading-text">
				Loading...
			</div>
		</div>
		`;
	}

	handleResponse (response) {
		const html = document.createElement('div');
		html.innerHTML = response;

		const data = html.querySelector(this.atwlQuerySelector);
		const success = html.querySelector('.js-atwl-success');

		if (success) {
			this.triggerGTMEvent();
		}

		if (success && typeof this.successCallback === 'function') {
			this.successCallback(response);
		} else if (data) {
			this.showloadingOverlay(false);

			this.showTippy(data.innerHTML);

			this.bindResponseEvents();

			if (success) {
				setTimeout(() => {
					this.hideTippy();
				}, 2000);
			}
		}
	}

	triggerGTMEvent () {
		const formData = new FormData(this.formElement);

		let gtmPrice = parseFloat(formData.get('Product_Price') || 0);
		const priceElement = document.getElementById('price-value');
		if (priceElement) {
			gtmPrice = parseFloat(priceElement.innerHTML.replace(/[^0-9.-]+/g, '') || 0);
		}
		const quantity = parseFloat(formData.get('Quantity')) || 1;
		const gtmValue = parseFloat((gtmPrice * quantity).toFixed(2));

		window.dataLayer.push({
			ecommerce: null
		});

		window.dataLayer.push({
			ecommerce: {
				items: [
					{
						/* eslint-disable camelcase */
						item_id: formData.get('Product_Code'),
						item_name: formData.get('Product_Name'),
						/* eslint-enable camelcase */
						price: gtmPrice,
						quantity: parseFloat(formData.get('Quantity')) || 1
					}
				],
				value: gtmValue
			},
			event: 'add_to_wishlist'
		});
	}

	bindResponseEvents () {
		const wishlistButtons = this.formElement.getElementsByClassName('js-atwl-wishlist');
		const newWishlistButtons = this.formElement.getElementsByClassName('js-atwl-new');
		const cancelCreateWishlistButtons = this.formElement.getElementsByClassName('js-atwl-create-cancel');
		const createWishlistButtons = this.formElement.getElementsByClassName('js-atwl-create-wishlist');

		for (let wishlistButton of wishlistButtons) {
			wishlistButton.addEventListener('click', () => this.wishlistButtonClick(wishlistButton, wishlistButtons, newWishlistButtons), false);
		}

		for (let newWishlistButton of newWishlistButtons) {
			newWishlistButton.addEventListener('click', () => this.showCreateForm(true), false);
		}

		for (let cancelCreateWishlistButton of cancelCreateWishlistButtons) {
			cancelCreateWishlistButton.addEventListener('click', () => this.showCreateForm(false), false);
		}

		for (let createWishlistButton of createWishlistButtons) {
			createWishlistButton.addEventListener('click', () => this.createWishlistButtonClick(createWishlistButtons, cancelCreateWishlistButtons), false);
		}

	}

	showCreateForm (show) {
		const createWishlistForm = this.formElement.querySelector('.js-atwl-wishlist-form');

		const wishlistTitle = createWishlistForm.querySelector('[name="WishList_Title"]');
		const wishlistNotes = createWishlistForm.querySelector('[name="WishList_Notes"]');

		if (show) {
			wishlistTitle.disabled = false;
			wishlistNotes.disabled = false;

			createWishlistForm.classList.add('t-atwl-create-form--show');

			wishlistTitle.focus();
		} else {
			wishlistTitle.disabled = true;
			wishlistNotes.disabled = true;

			createWishlistForm.classList.remove('t-atwl-create-form--show');
		}
	}

	async wishlistButtonClick (button, allButtons, createButton) {

		const wishlistID = button.getAttribute('data-id');

		for (let b of allButtons) {
			b.disabled = true;
		}

		createButton.disabled = true;

		this.showloadingOverlay(true);

		this.actionInputElement.value = this.action;

		const formData = new FormData(this.formElement);

		if (formData.hasOwnProperty('WishList_ID')) {
			formData.WishList_ID = wishlistID;
		} else {
			formData.append('WishList_ID', wishlistID);
		}

		const response = await this.submitRequest(this.formElement.action, formData);

		this.resetAction();

		this.handleResponse(response);
	}

	async createWishlistButtonClick (buttons, cancelButton) {
		for (let button of buttons) {
			const buttonPoperty = button instanceof HTMLButtonElement ? 'textContent' : 'value';
			button[buttonPoperty] = 'Processing...';

			button.disabled = true;
		}

		cancelButton.disabled = true;

		const formElem = this.formElement.querySelector('.js-atwl-wishlist-form');
		const action = this.formElement.action;
		const wishlistTitle = formElem.querySelector('[name="WishList_Title"]').value;
		const wishlistNotes = formElem.querySelector('[name="WishList_Notes"]').value;
		const data = new FormData();

		data.append('Action', 'IWSH');
		data.append('WishList_Title', wishlistTitle);
		data.append('WishList_Notes', wishlistNotes);

		const iwshResponse = await this.submitRequest(action, data);

		const html = document.createElement('div');
		html.innerHTML = iwshResponse;

		const wishlistIDElem = html.querySelector('#js-iwsh-id');

		if (wishlistIDElem) {
			const wishlistID = wishlistIDElem.getAttribute('data-id');

			this.actionInputElement.value = this.action;

			const formData = new FormData(this.formElement);

			if (formData.hasOwnProperty('WishList_ID')) {
				formData.WishList_ID = wishlistID;
			} else {
				formData.append('WishList_ID', wishlistID);
			}

			const atwlResponse = await this.submitRequest(this.formElement.action, formData);

			this.resetAction();

			this.handleResponse(atwlResponse);
		} else {
			this.handleResponse(iwshResponse);
		}
	}

	resetAction () {
		this.actionInputElement.value = this.original_action;
	}

	createTippy (button) {
		tippy(button, {
			allowHTML: true,
			arrow: false,
			content: '',
			interactive: true,
			maxWidth: 'none',
			placement: 'bottom-end',
			theme: 'add-to-wishlist',
			trigger: 'manual',
			zIndex: 2
		});
	}

	showloadingOverlay (show) {
		if (this.currentButton && this.currentButton._tippy) {
			if (show) {
				this.currentButton._tippy.popper.classList.add('t-atwl--loading-overlay');
			} else {
				this.currentButton._tippy.popper.classList.remove('t-atwl--loading-overlay');
			}
		}
	}

	isVisible (elem) {
		const style = window.getComputedStyle(elem);

		return (
			style.width !== '0' &&
			style.height !== '0' &&
			style.opacity !== '0' &&
			style.display !== 'none' &&
			style.visibility !== 'hidden'
		);
	}
}
